import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  LocationCreate,
  LocationRead,
  LocationUpdate,
  type LocationCreate as LocationCreateT,
  type LocationUpdate as LocationUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const LocationList = z.array(LocationRead);

export function useLocations(projectId: number) {
  return useQuery({
    queryKey: qk.locations(projectId),
    queryFn: () => api.get(`/api/projects/${projectId}/locations`, LocationList),
    enabled: projectId > 0,
  });
}

export function useCreateLocation(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LocationCreateT) =>
      api.post(
        `/api/projects/${projectId}/locations`,
        LocationCreate.parse(input),
        LocationRead,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.locations(projectId) }),
  });
}

export function useUpdateLocation(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: LocationUpdateT }) =>
      api.patch(`/api/locations/${id}`, LocationUpdate.parse(patch), LocationRead),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.locations(projectId) }),
  });
}

export function useDeleteLocation(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/locations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.locations(projectId) }),
  });
}
